import { sendGTMEvent } from "@next/third-parties/google";

import type {
  Channel,
  Coverage,
  OfferViewModel,
  PolicyHolder,
  ProductType,
  Quote,
  WebShopPublicUser,
} from "@/api_client";

import { createAddToCart } from "../events/createAddToCart";
import { createAddToCartButton } from "../events/createAddToCartButton";
import { createCheckoutEvent } from "../events/createCheckoutEvent";
import { createCheckoutInterruptedEvent } from "../events/createCheckoutInteruptedEvent";
import {
  type CheckoutOptionEventParams,
  createCheckoutOptionEvent,
} from "../events/createCheckoutOptionEvent";
import { createInsuranceInformationUpdated } from "../events/createInsuranceInformationUpdated";
import {
  createPageLoad,
  createPageLoadMultipleProducts,
  type PageLoadedMultipleProductsParams,
  type PageLoadedParams,
} from "../events/createPageLoad";
import { createPriceCalculation } from "../events/createPriceCalculation";
import { createProductPageImpression } from "../events/createProductPageImpression";
import { createPurchaseEvent } from "../events/createPurchaseEvent";
import { createRemoveFromCart } from "../events/createRemoveFromCart";
import { createUserInteractionEvent } from "../events/createUserInteractionEvent";
import { createVerifyAndBuyEvent } from "../events/createVerifyAndBuyEvent";
import { createWebshopCalculatePriceButton } from "../events/createWebshopCalculatePriceButton";
import { createWebshopEditDetailsButton } from "../events/createWebshopEditDetailsButton";
import { createWebshopEntryFormButton } from "../events/createWebshopEntryFormButton";
import { createWebshopRemoveFromCartButton } from "../events/createWebshopRemoveFromCartButton";

const pageLoaded = (pageLoadParams: PageLoadedParams) => {
  const event = createPageLoad(pageLoadParams);
  sendGTMEvent(event);
};

const pageLoadedMultiProduct = (
  pageLoadParams: PageLoadedMultipleProductsParams,
) => {
  const event = createPageLoadMultipleProducts(pageLoadParams);
  sendGTMEvent(event);
};

const productPageImpression = (
  contextId: string,
  productType: ProductType,
  channel: Channel,
) => {
  const event = createProductPageImpression(contextId, productType, channel);
  sendGTMEvent(event);
};

const entryFormClick = () => {
  const event = createWebshopEntryFormButton();
  sendGTMEvent(event);
};

const shadowDomEntryClick = (label: string, status: string) => {
  const event = createUserInteractionEvent({ label, status });
  sendGTMEvent(event);
};

const priceCalculated = (
  contextId: string,
  product: Quote,
  enigmatizedProduct: Quote | undefined,
  channel: Channel,
  enigmatizedPolicyHolder: PolicyHolder,
  user: WebShopPublicUser,
  coverageContent: Record<string, Coverage> | undefined,
) => {
  const event = createPriceCalculation(
    contextId,
    product,
    enigmatizedPolicyHolder,
    enigmatizedProduct,
    channel,
    user,
    coverageContent,
  );
  if (!event) {
    return;
  }
  sendGTMEvent(event);
};

const insuranceInformationUpdated = (
  contextId: string,
  product: Quote,
  enigmatizedProduct: Quote | undefined,
  enigmatizedPolicyHolder: PolicyHolder | undefined | null,
  user: WebShopPublicUser | undefined,
  coverageContent: Record<string, Coverage> | undefined,
) => {
  const event = createInsuranceInformationUpdated(
    contextId,
    product,
    enigmatizedProduct,
    enigmatizedPolicyHolder,
    user,
    coverageContent,
  );
  if (!event) {
    return;
  }
  sendGTMEvent(event);
};

const editDetailsClick = () => {
  const event = createWebshopEditDetailsButton();
  sendGTMEvent(event);
};

const addToCartClick = (
  contextId: string,
  product: Quote,
  enigmatizedPolicyHolder: PolicyHolder | undefined | null,
  enigmatizedProduct: Quote | undefined,
  channel: Channel,
  coverageContent: Record<string, Coverage> | undefined,
) => {
  const addToBasketButtonEvent = createAddToCartButton();
  sendGTMEvent(addToBasketButtonEvent);
  const event = createAddToCart(
    contextId,
    enigmatizedPolicyHolder,
    product,
    enigmatizedProduct,
    channel,
    coverageContent,
  );
  if (!event) {
    return;
  }
  sendGTMEvent(event);
};

const removeFromCartClick = (
  product: Quote,
  channel: Channel,
  coverageContent: Record<string, Coverage> | undefined,
) => {
  const clickEvent = createWebshopRemoveFromCartButton();
  sendGTMEvent(clickEvent);
  const event = createRemoveFromCart(product, channel, coverageContent);
  if (!event) {
    return;
  }
  sendGTMEvent(event);
};

const calculatePriceClick = () => {
  const event = createWebshopCalculatePriceButton();
  sendGTMEvent(event);
};

const checkoutInitiated = (
  contextId: string,
  offer: OfferViewModel,
  channel: Channel,
  coverageContent: Record<string, Coverage> | undefined,
) => {
  const event = createCheckoutEvent(contextId, offer, channel, coverageContent);
  if (!event) {
    return;
  }
  sendGTMEvent(event);
};

const checkoutOptionChanged = (params: CheckoutOptionEventParams) => {
  const event = createCheckoutOptionEvent(params);
  if (!event) {
    return;
  }
  sendGTMEvent(event);
};

const verifyAndBuyClick = (
  offer: OfferViewModel,
  channel: Channel,
  coverageContent: Record<string, Coverage> | undefined,
) => {
  const event = createVerifyAndBuyEvent(offer, channel, coverageContent);
  if (!event) {
    return;
  }
  sendGTMEvent(event);
};

const checkoutInterrupted = (status: number | undefined) => {
  const event = createCheckoutInterruptedEvent(status);
  sendGTMEvent(event);
};

const purchaseCompleted = (
  contextId: string,
  offer: OfferViewModel,
  channel: Channel,
  coverageContent: Record<string, Coverage> | undefined,
) => {
  const event = createPurchaseEvent(contextId, offer, channel, coverageContent);
  if (!event) {
    return;
  }
  sendGTMEvent(event);
};

export const dataLayerPusher = {
  pageLoaded,
  pageLoadedMultiProduct,
  productPageImpression,
  entryFormClick,
  shadowDomEntryClick,
  editDetailsClick,
  addToCartClick,
  calculatePriceClick,
  priceCalculated,
  insuranceInformationUpdated,
  removeFromCartClick,
  checkoutInitiated,
  checkoutOptionChanged,
  verifyAndBuyClick,
  purchaseCompleted,
  checkoutInterrupted,
};
